'use client';

import Loader from '@/components/ui/Loader';
import { useFestivalContext } from '@/contexts/FestivalContext';
import { useUserStore } from '@/store/useUserStore';
import { createClient } from '@/utils/supabase/client';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import OrderRowItem, { Order } from '../components/OrderRowItem';
import OrdersTableSortingBar from '../components/OrdersTableSortingBar';

export default function OrderHistory({ params: { locale } }: { params: { locale: string } }) {
    const t = useTranslations();
    const supabase = useMemo(() => createClient(), []);
    const { slug: festivalSlug, festival } = useFestivalContext();
    const [ordersData, setOrdersData] = useState<Order[] | null>(null);
    const { user } = useUserStore();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user && festival?.id) {
            const fetchOrders = async () => {
                // First, get unique order IDs for this festival
                const { data: allocations, error: allocError } = await supabase
                    .from('ticket_allocations')
                    .select('order_id')
                    .eq('user_id', user.id)
                    .eq('festival_id', festival.id)
                    .not('order_id', 'is', null);

                if (allocError) console.error(allocError);

                const orderIds =
                    (allocations?.map((a) => a.order_id).filter(Boolean) as string[]) || [];

                if (!orderIds.length) {
                    setOrdersData([]);
                    setLoading(false);
                    return;
                }

                // Get orders for this user
                const { data: ordersData, error: ordersDataError } = await supabase
                    .rpc('get_user_orders', {
                        user_email: user?.email!,
                        locale,
                    })
                    .returns<Order[]>();

                if (!ordersData || ordersDataError) console.error(ordersDataError);

                if (ordersData) {
                    // Filter by festival through order_id
                    const filteredOrders = ordersData
                        .filter((order) => order.status === 'paid')
                        .filter((order) => orderIds.includes(order.order_id));

                    // Sort in reverse order (newest first)
                    filteredOrders.sort((a, b) => {
                        const dateA = new Date(a.created_at ?? '').getTime();
                        const dateB = new Date(b.created_at ?? '').getTime();
                        return dateB - dateA;
                    });

                    setOrdersData(filteredOrders);
                } else {
                    setOrdersData(null);
                }
                setLoading(false);
            };

            fetchOrders();
        }
    }, [user, festival?.id, locale, supabase]);

    return (
        <div className="flex flex-col gap-12 mt-12 max-xl:w-[calc(100%+40px)]">
            <div className="flex flex-col gap-3 lg:gap-7">
                {loading ? (
                    <Loader />
                ) : (
                    <div className="flex flex-col max-xl:overflow-auto">
                        {ordersData?.length ? (
                            <>
                                <OrdersTableSortingBar />
                                {ordersData.map((props) => (
                                    <OrderRowItem {...props} key={props.order_id} />
                                ))}
                            </>
                        ) : (
                            <div className="border border-white/50 w-full p-7 flex flex-col justify-center items-center max-xl:w-[calc(100%-40px)]">
                                <h5 className="uppercase text-h5 text-white/70 w-5/6 text-center">
                                    {t('Common.oops')}
                                </h5>
                                <h5 className="uppercase lg:text-h5 text-[22px] text-white/70 w-5/6 text-center">
                                    {t('Common.outOfOrders')}
                                </h5>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
